import React, { useMemo, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { getTranslatedString } from 'helpers/i18n';
import TenantResidentVerbiageHelper from 'components/shared/TenantResidentVerbiageHelper';
import { getActiveLeaseOfUnit } from 'actions/landlordPropertiesActions';
import { hostIsCapreit } from 'helpers/CurrentWindowLocation';

import Form from 'components/ui/Form';
import { isLegalNotice, isNoticeOfEntryNotice, modes } from '../NoticeHelpers';
import MultiSelect from '../../../NoticeMultiselect';

const FullWidthSelect = styled(MultiSelect)`
  width: 100%;
`;

const CotenantsInput = ({
  values,
  errors,
  touched,
  setFieldValue,
  unitActiveLease,
  getActiveLeaseOfUnit,
}) => {
  const { mode } = values;

  const allCotenantsMustBeSelected = useMemo(() => {
    if (!hostIsCapreit()) return false;

    // Any Capreit logic to check if all cotenants are automatically selected
    if (isLegalNotice(values) || isNoticeOfEntryNotice(values)) {
      return true;
    }
  }, [values.notice_type, hostIsCapreit, isLegalNotice]);

  // Reload the units options whenever the properties selection changes.
  useEffect(() => {
    if (values.units.length !== 1) return;

    getActiveLeaseOfUnit(values.units[0]);
  }, [values.units, values.mode, getActiveLeaseOfUnit]);

  useEffect(() => {
    if (!unitActiveLease) {
      return;
    }
    if (allCotenantsMustBeSelected) {
      handleChange(cotenantOptions.map((o) => o.value));
    }
  }, [unitActiveLease]);

  const cotenantOptions = useMemo(() => {
    if (!unitActiveLease) return [];

    let cotenants = unitActiveLease.cotenants;
    if (isLegalNotice(values)) {
      cotenants = cotenants.filter((c) => !!c.digital_legal_documents);
    } else if (isNoticeOfEntryNotice(values)) {
      cotenants = cotenants.filter((c) => !!c.notice_by_email);
    }
    cotenants = cotenants.filter((c) => c.status === "active")
    return cotenants.map((c) => ({ label: c.full_name, value: c.id }));
  }, [values, unitActiveLease]);

  const handleChange = (cotenants) => {
    let setCotenants = cotenants || [];
    setCotenants = Array.isArray(setCotenants) ? setCotenants : [setCotenants];
    setFieldValue('cotenants', setCotenants);
  };

  if (!unitActiveLease) return null;
  if (mode !== modes.SELECTING_UNIT_COTENANTS) return null;
  // Can only show the unit selection on a single selected property
  if (values.properties.length === 0 || values.properties.length > 1) {
    return null;
  }

  const labelVerbiage = (
    <TenantResidentVerbiageHelper
      translatedTenant={getTranslatedString('landlordPortal.noticeAssignmentForm.cotenantsTenant')}
      translatedResident={getTranslatedString(
        'landlordPortal.noticeAssignmentForm.cotenantsResident'
      )}
    />
  );

  return (
    <Form.Item
      label={labelVerbiage}
      validateStatus={touched.cotenants && errors.cotenants ? 'error' : null}
      help={touched.cotenants && errors.cotenants}
    >
      <FullWidthSelect
        options={cotenantOptions}
        name="cotenants"
        fullWidth
        withResetSelectAllButtons
        multiple={true}
        value={values.cotenants}
        error={touched.cotenants && errors.cotenants}
        onChange={handleChange}
        disabled={allCotenantsMustBeSelected}
      />
    </Form.Item>
  );
};

export default connect(
  (store) => {
    return {
      unitActiveLease: store.landlordProperties.unitActiveLease,
      noticeTemplates: store.landlordNotices.notice_templates,
    };
  },
  {
    getActiveLeaseOfUnit,
  }
)(CotenantsInput);
